import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FileNameWithIcon from "../../../../components/fileNameWithIcon";
import { portfolioType, rateCardType } from "utils/constantValues";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import useUploadFiles from "pages/signup/hooks/useUploadFiles";
import UploadFile from "components/uploadFile";
import { ReactComponent as AttachIcon } from "images/auth/attach.svg";
import useProfileHelper from "pages/profile/useProfileHelper";
import usePortfolioFiles from "pages/profile/agencyProfile/portfolioFiles/usePortfolio";
import useRateCardFile from "pages/profile/agencyProfile/rateCardFile/useRateCardFile";

function PortfolioInformation({ onSave = () => {} }) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});
  const defaultPortfolioFiles = userDetails?.userFiles
    ?.filter((file) => file.type === portfolioType)
    .map((file) => ({ ...file, fileId: file.id }));

  const defaultRateCardFile = userDetails?.userFiles
    ?.filter((file) => file.type === rateCardType)
    .map((file) => ({ ...file, fileId: file.id }));
  const defaultsValues = {
    newCreated: [],
    deleted: [],
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultsValues,
  });

  const {
    isUploading,
    portfolioRef,
    uploadPortfolio,
    removePortfolioFile,
    rateCardRef,
    uploadRateCard,
    removeRateCardFile,
  } = useUploadFiles({
    defaultPortfolioFiles: defaultPortfolioFiles,
    defaultRateCard: defaultRateCardFile,
  });

  const { onEditRateCardFile } = useRateCardFile({});

  const { onEditPortfolioFiles } = usePortfolioFiles({});

  const { downloadFileInANewTab } = useProfileHelper();

  const isRequired = defaultRateCardFile?.length > 0;

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] "
      onSubmit={handleSubmit(onSave)}
    >
      <div
        id="certificates-information"
        className={`flex p-[24px]  gap-[24px] self-stretch`}
      >
        <div className="flex items-start gap-6 self-stretch">
          <div className="flex flex-col items-start w-[330px] gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[#1D1B22] text-center font-semibold text-[24px] leading-[36px] font-['General_Sans']">
                {t("portfolio")}
              </span>
            </div>

            <div className="grid  gap-[12px] w-full">
              <UploadFile
                label="Document"
                name={"portfolio"}
                register={register}
                isMultiFile={true}
                placeHolderLabel={t("attachYourFiles")}
                placeHolderContainerClassName="flex p-[14px] justify-between items-center gap-[8px] self-stretch rounded-[6px] h-[54px] w-full border-[1px] border-dashed border-dark-gray cursor-pointer"
                icon={<AttachIcon />}
                isUploading={isUploading.portfolio}
                getFile={(e, isDrag) => {
                  const callback = (type, files) => {
                    setValue(type, files);
                    setValue("newCreated", files);

                    onEditPortfolioFiles({ newCreated: files });
                  };
                  uploadPortfolio(e, isDrag, callback, true);
                }}
                labelClassName="text-primary font-semibold text-[16px]"
                ref={portfolioRef}
                registerArgs={{
                  required: t("errorField", { field: t("portfolio") }),
                }}
                isRequired={true}
              />
            </div>

            <div className="flex flex-wrap">
              {defaultPortfolioFiles.map((file, index) => (
                <>
                  <FileNameWithIcon
                    fileName={file.name}
                    showHover={true}
                    onDeleteHandler={() => {
                      const prevDeletedFiles = watch("deleted") || [];

                      const deletedFile = defaultPortfolioFiles.filter(
                        (file) => file.name
                      );
                      setValue("deleted", [
                        ...prevDeletedFiles,
                        ...deletedFile,
                      ]);

                      onEditPortfolioFiles({
                        deleted: [{ fileId: file.fileId }],
                      });
                      removePortfolioFile(file.name);
                    }}
                    onDownloadHandler={() => downloadFileInANewTab(file.fileId)}
                    withoutBorder={defaultPortfolioFiles?.length === index + 1}
                  />
                </>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-6 self-stretch">
          <div className="flex flex-col items-start w-[330px] gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[#1D1B22] text-center font-semibold text-[24px] leading-[36px] font-['General_Sans']">
                {t("rateCard")}
              </span>
            </div>

            <div className="grid  gap-[12px]  w-full">
              <UploadFile
                label="Document"
                name={"rateCard"}
                register={register}
                isMultiFile={true}
                registerArgs={{
                  required: t("errorField", { field: t("rateCard") }),
                }}
                isRequired={true}
                errorMessage={errors.rateCard ? errors.rateCard.message : ""}
                placeHolderLabel={t("attachYourFiles")}
                placeHolderContainerClassName="flex p-[14px] justify-between items-center gap-[8px] self-stretch rounded-[6px] h-[54px] w-full border-[1px] border-dashed border-dark-gray cursor-pointer"
                icon={<AttachIcon />}
                isUploading={isUploading.rateCard}
                files={defaultRateCardFile}
                getFile={(e, isDrag) => {
                  const callback = (type, files) => {
                    setValue(type, files);
                    setValue("newCreated", files);
                    onEditRateCardFile({ newCreated: files });
                  };
                  uploadRateCard(e, isDrag, callback, true);
                }}
                labelClassName="text-primary font-semibold text-[16px]"
                ref={rateCardRef}
              />

              <div className="flex flex-wrap">
                {defaultRateCardFile.map((file, index) => (
                  <>
                    <FileNameWithIcon
                      fileName={file.name}
                      showHover={true}
                      onDeleteHandler={() => {
                        const prevDeletedFiles = watch("deleted") || [];

                        const deletedFile = defaultRateCardFile.filter(
                          (file) => file.name
                        );
                        setValue("deleted", [
                          ...prevDeletedFiles,
                          ...deletedFile,
                        ]);

                        onEditRateCardFile({
                          deleted: [{ fileId: file.fileId }],
                        });
                        removeRateCardFile(file.name);
                      }}
                      onDownloadHandler={() =>
                        downloadFileInANewTab(file.fileId)
                      }
                      withoutBorder={defaultRateCardFile?.length === index + 1}
                    />
                  </>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

PortfolioInformation.propTypes = {
  onEditClick: PropTypes.func,
  isShowEditIcon: PropTypes.bool,
};

export default PortfolioInformation;
